import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { Upload, CheckCircle, AlertCircle, FileText } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InsuranceUpload = ({ carrierEmail, onComplete }) => {
  const [insurances, setInsurances] = useState({
    public_liability: { file: null, policyNumber: '', insurerName: '', expiryDate: '', uploaded: false },
    motor_fleet: { file: null, policyNumber: '', insurerName: '', expiryDate: '', uploaded: false },
    marine_carriers: { file: null, policyNumber: '', insurerName: '', expiryDate: '', uploaded: false },
    workers_comp: { file: null, policyNumber: '', insurerName: '', expiryDate: '', uploaded: false },
    trailers_in_control: { file: null, policyNumber: '', insurerName: '', expiryDate: '', uploaded: false },
    nhvr_accreditation: { file: null, policyNumber: '', insurerName: '', expiryDate: '', uploaded: false },
    wahva_accreditation: { file: null, policyNumber: '', insurerName: '', expiryDate: '', uploaded: false },
    trucksafe_cor: { file: null, policyNumber: '', insurerName: '', expiryDate: '', uploaded: false },
    haccp_accreditation: { file: null, policyNumber: '', insurerName: '', expiryDate: '', uploaded: false },
    msic_pass: { file: null, policyNumber: '', insurerName: '', expiryDate: '', uploaded: false },
    dangerous_goods: { file: null, policyNumber: '', insurerName: '', expiryDate: '', uploaded: false },
    ports_pass: { file: null, policyNumber: '', insurerName: '', expiryDate: '', uploaded: false },
    high_risk_work: { file: null, policyNumber: '', insurerName: '', expiryDate: '', uploaded: false }
  });
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});

  const insuranceTypes = [
    { key: 'public_liability', label: 'Public Liability Insurance', required: true, type: 'insurance', fieldLabel: 'Policy Number' },
    { key: 'motor_fleet', label: 'Motor/Fleet Insurance', required: false, type: 'insurance', fieldLabel: 'Policy Number' },
    { key: 'marine_carriers', label: 'Marine Carriers Insurance', required: true, type: 'insurance', fieldLabel: 'Policy Number' },
    { key: 'workers_comp', label: 'Workers Compensation Insurance', required: true, type: 'insurance', fieldLabel: 'Policy Number' },
    { key: 'trailers_in_control', label: 'Trailers in Control Insurance', required: false, type: 'insurance', fieldLabel: 'Policy Number' },
    { key: 'nhvr_accreditation', label: 'NHVR Accreditation Certificate', required: false, type: 'certificate', fieldLabel: 'Accreditation Number' },
    { key: 'wahva_accreditation', label: 'WAHVA Accreditation Certificate', required: false, type: 'certificate', fieldLabel: 'Accreditation Number' },
    { key: 'trucksafe_cor', label: 'TruckSafe/COR Certificate', required: false, type: 'certificate', fieldLabel: 'Accreditation Number' },
    { key: 'haccp_accreditation', label: 'HACCP Accreditation Certificate', required: false, type: 'certificate', fieldLabel: 'Accreditation Number' },
    { key: 'msic_pass', label: 'MSIC Pass', required: false, type: 'licence', fieldLabel: 'Licence Number' },
    { key: 'dangerous_goods', label: 'Dangerous Goods Licencing', required: false, type: 'licence', fieldLabel: 'Licence Number' },
    { key: 'ports_pass', label: 'Ports Pass', required: false, type: 'licence', fieldLabel: 'Licence Number' },
    { key: 'high_risk_work', label: 'High Risk Work Licences', required: false, type: 'licence', fieldLabel: 'Licence Number' }
  ];

  const handleFileChange = (type, file) => {
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors({ ...errors, [type]: 'File size must be less than 5MB' });
        return;
      }
      
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        setErrors({ ...errors, [type]: 'Only PDF, JPG, and PNG files are allowed' });
        return;
      }

      setInsurances({
        ...insurances,
        [type]: { ...insurances[type], file }
      });
      setErrors({ ...errors, [type]: null });
    }
  };

  const handleInputChange = (type, field, value) => {
    setInsurances({
      ...insurances,
      [type]: { ...insurances[type], [field]: value }
    });
  };

  const validateExpiryDate = (dateString) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiryDate = new Date(dateString);
    return expiryDate > today;
  };

  const uploadInsurance = async (type) => {
    const insurance = insurances[type];
    const insuranceTypeConfig = insuranceTypes.find(t => t.key === type);
    
    // Validation
    if (!insurance.file) {
      setErrors({ ...errors, [type]: 'Please select a file' });
      return;
    }
    if (!insurance.policyNumber) {
      setErrors({ ...errors, [type]: 'Please enter policy number' });
      return;
    }
    // Only validate insurer name for insurance types, not for certificates or licences
    if (insuranceTypeConfig?.type === 'insurance' && !insurance.insurerName) {
      setErrors({ ...errors, [type]: 'Please enter insurer name' });
      return;
    }
    if (!insurance.expiryDate) {
      setErrors({ ...errors, [type]: 'Please enter expiry date' });
      return;
    }
    if (!validateExpiryDate(insurance.expiryDate)) {
      setErrors({ ...errors, [type]: 'Insurance must not be expired' });
      return;
    }

    setUploading(true);
    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.readAsDataURL(insurance.file);
      
      reader.onload = async () => {
        const base64String = reader.result.split(',')[1];
        
        const payload = {
          carrier_email: carrierEmail,
          insurance_type: type,
          policy_number: insurance.policyNumber,
          insurer_name: insurance.insurerName,
          expiry_date: insurance.expiryDate,
          document_base64: base64String,
          file_name: insurance.file.name
        };

        const response = await axios.post(`${API}/insurance/upload`, payload);
        
        setInsurances({
          ...insurances,
          [type]: { ...insurance, uploaded: true }
        });
        
        setErrors({ ...errors, [type]: null });
      };
    } catch (error) {
      setErrors({ 
        ...errors, 
        [type]: error.response?.data?.detail || 'Failed to upload insurance'
      });
    } finally {
      setUploading(false);
    }
  };

  const allUploaded = () => {
    // Only check required insurances
    const requiredTypes = insuranceTypes.filter(type => type.required);
    return requiredTypes.every(type => insurances[type.key].uploaded);
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">Insurance & Certification Requirements</h3>
        <p className="text-sm text-blue-800 mb-2">
          All carriers must provide the following <strong>required</strong> insurance documents before completing signup:
        </p>
        <ul className="text-sm text-blue-800 list-disc ml-5 mb-2">
          <li>Public Liability Insurance</li>
          <li>Marine Carriers Insurance</li>
          <li>Workers Compensation Insurance</li>
        </ul>
        <p className="text-sm text-blue-800">
          Additional certifications and licenses are <strong>optional</strong> but may increase your job opportunities.
        </p>
      </div>

      {insuranceTypes.map((insuranceType) => (
        <Card key={insuranceType.key} className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-gray-600" />
              <h4 className="font-semibold text-gray-900">{insuranceType.label}</h4>
              {insuranceType.required && (
                <span className="text-red-600 text-sm">*Required</span>
              )}
              {!insuranceType.required && (
                <span className="text-gray-500 text-sm">Optional</span>
              )}
            </div>
            {insurances[insuranceType.key].uploaded && (
              <CheckCircle className="w-6 h-6 text-green-600" />
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label htmlFor={`${insuranceType.key}-policy`}>{insuranceType.fieldLabel} *</Label>
              <Input
                id={`${insuranceType.key}-policy`}
                value={insurances[insuranceType.key].policyNumber}
                onChange={(e) => handleInputChange(insuranceType.key, 'policyNumber', e.target.value)}
                placeholder={`e.g., ${insuranceType.type === 'insurance' ? 'PL-123456' : insuranceType.type === 'certificate' ? 'ACC-123456' : 'LIC-123456'}`}
                disabled={insurances[insuranceType.key].uploaded}
              />
            </div>

            {insuranceType.type === 'insurance' && (
              <div>
                <Label htmlFor={`${insuranceType.key}-insurer`}>Insurer Name *</Label>
                <Input
                  id={`${insuranceType.key}-insurer`}
                  value={insurances[insuranceType.key].insurerName}
                  onChange={(e) => handleInputChange(insuranceType.key, 'insurerName', e.target.value)}
                  placeholder="e.g., ABC Insurance Co."
                  disabled={insurances[insuranceType.key].uploaded}
                />
              </div>
            )}

            <div>
              <Label htmlFor={`${insuranceType.key}-expiry`}>Expiry Date *</Label>
              <Input
                id={`${insuranceType.key}-expiry`}
                type="date"
                value={insurances[insuranceType.key].expiryDate}
                onChange={(e) => handleInputChange(insuranceType.key, 'expiryDate', e.target.value)}
                disabled={insurances[insuranceType.key].uploaded}
              />
            </div>

            <div>
              <Label htmlFor={`${insuranceType.key}-file`}>Upload Document (PDF, JPG, PNG) *</Label>
              <Input
                id={`${insuranceType.key}-file`}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleFileChange(insuranceType.key, e.target.files[0])}
                disabled={insurances[insuranceType.key].uploaded}
                className="cursor-pointer"
              />
              {insurances[insuranceType.key].file && (
                <p className="text-xs text-gray-600 mt-1">
                  Selected: {insurances[insuranceType.key].file.name}
                </p>
              )}
            </div>
          </div>

          {errors[insuranceType.key] && (
            <div className="flex items-center gap-2 text-red-600 text-sm mb-3">
              <AlertCircle className="w-4 h-4" />
              {errors[insuranceType.key]}
            </div>
          )}

          {!insurances[insuranceType.key].uploaded && (
            <Button
              onClick={() => uploadInsurance(insuranceType.key)}
              disabled={uploading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'Uploading...' : 'Upload Document'}
            </Button>
          )}

          {insurances[insuranceType.key].uploaded && (
            <div className="flex items-center gap-2 text-green-600 text-sm">
              <CheckCircle className="w-4 h-4" />
              Document uploaded successfully (pending admin verification)
            </div>
          )}
        </Card>
      ))}

      {allUploaded() && onComplete && (
        <Button
          onClick={onComplete}
          className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg"
        >
          Continue to Payment
        </Button>
      )}
    </div>
  );
};

export default InsuranceUpload;
